import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Card, Col, Container, Modal, Row } from "react-bootstrap";

import classNames from "classnames";
import { isUndefined } from "lodash";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";

import {
  ParticipantStatus,
  ParticipantTypes,
  RequestSource,
  RssDocument,
  useGetPreSignedUploadUrlMutation,
  useInsertOrUpdateResalesBatchMutation,
  useNotifyUploadCompletionMutation,
  useUpdateReviewedStatusMutation,
} from "@api/graphql";
import { CrossIconBlue, PlusIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { FileDisplay } from "@components/grouped_resales/FileDisplay";
import { ReporterDisplay } from "@components/grouped_resales/ReporterDisplay";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { ResaleSubmitted } from "@components/resale_submission/ResaleSubmitted";
import { SubmitResalesDisclaimer } from "@components/resale_submission/SubmitResalesDisclaimer";
import { CartDrawerContent, CartDrawerContentProps } from "@components/resale_submission/SummaryCart/CartDrawer";
import { ValidatedResaleInput } from "@components/resale_submission/csv/ParseFiles";
import { ExternalRoles, Role } from "@roles";
import {
  batchUploadDocument,
  curatedUploadDocument,
  resaleFormData,
  selectedParticipant,
} from "@states/resale_submission";
import { useWatch } from "@util/index";

import { ROUTES } from "../../routes";

import { DashboardHeader } from "./DashboardHeader";

import styles from "./SubmitAllResales.module.scss";

interface OptionalProps {
  input?: ValidatedResaleInput[];
  batchDocument?: RssDocument;
  curatedDocument?: File;
}

export const SubmitAllResales = ({ input, batchDocument, curatedDocument }: OptionalProps) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [numSubmitted, setNumSubmitted] = useState<number>(0);
  const [resaleData, setResaleData] = useRecoilState(resaleFormData);
  const [batchDoc, setBatchDoc] = useRecoilState(batchUploadDocument);
  const [curatedDoc, setCuratedDoc] = useRecoilState(curatedUploadDocument);
  const [participant, setParticipant] = useRecoilState(selectedParticipant);
  const [modalShow, setModalShow] = useState(false);
  const resetList = useResetRecoilState(resaleFormData);
  const resetBatchDoc = useResetRecoilState(batchUploadDocument);
  const resetCuratedDoc = useResetRecoilState(curatedUploadDocument);
  const resetParticipant = useResetRecoilState(selectedParticipant);
  const navigate = useNavigate();
  const [getPresignedUploadURL] = useGetPreSignedUploadUrlMutation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [notifyUploadCompletionMutation] = useNotifyUploadCompletionMutation();
  const [reviewedStatusUpdateMutation] = useUpdateReviewedStatusMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const isVAAdmin = useIsAuthUserAllowed([Role.VisualArtistAdmin]);

  useEffect(() => {
    if (input !== undefined) {
      setResaleData(input);
    }
    if (batchDocument !== undefined) {
      setBatchDoc(batchDocument);
      if (participant === undefined) {
        setParticipant({
          participant_id: parseInt(batchDocument.participant_id!),
          full_name: batchDocument.participant_name!,
          company_name: batchDocument.participant_company!,
          participant_status: ParticipantStatus.ACTIVE,
          participant_types: [...batchDocument.participant_types!] as ParticipantTypes[],
        });
      }
    }
    if (curatedDocument !== undefined) {
      setCuratedDoc(curatedDocument);
    }
  }, [input, setResaleData, batchDocument, setBatchDoc, curatedDocument, setCuratedDoc, participant, setParticipant]);

  const [insertResalesMutation] = useInsertOrUpdateResalesBatchMutation();

  const resaleIsInvalid = (resale: ValidatedResaleInput) => {
    return resale.validation && !resale.validation.success;
  };

  const allResalesValid = () => {
    return (
      resaleData.filter((resale) => {
        return resaleIsInvalid(resale);
      }).length === 0
    );
  };

  const isCmo = batchDoc?.participant_types?.includes(ParticipantTypes.CMO);

  const resales: CartDrawerContentProps[] = useMemo(() => {
    return resaleData.map((item) => {
      return {
        owner: item.artist?.provisional_artist_name ?? "",
        title: item.artwork?.artwork_name ?? "",
        status: resaleIsInvalid(item) ? "Error" : "Completed",
        resaleStatus: item.resaleStatus,
        validation: item.validation,
        showEditAndDeleteButton: !isCmo,
      };
    });
  }, [resaleData, isCmo]);

  const invalidResales = resales.filter((resale) => resale.status === "Error");
  const validResales = resales.filter((resale) => resale.status !== "Error");

  useWatch(resaleData, () => {
    if (resaleData !== undefined && resaleData.length > 0) {
      setResaleData(
        resaleData.slice().sort((a, b) => {
          if (!isUndefined(a.validation) && !isUndefined(b.validation)) {
            if (!a.validation.success && b.validation.success) {
              return 1;
            }

            if (a.validation.success && b.validation.success) {
              return 0;
            }

            if (a.validation.success && !b.validation.success) {
              return -1;
            }
          }
          return 0;
        }),
      );
    }
  });

  const confirmationButtonText = `Submit ${resales.length} reports`;

  const uploadCuratedFile = useCallback(async () => {
    try {
      const { data } = await getPresignedUploadURL({
        variables: { fileName: curatedDoc.name, fileType: curatedDoc.type },
      });

      const uploadUrl = data?.getPreSignedUploadURL?.uploadUrl;
      const key = data?.getPreSignedUploadURL?.key;

      if (!uploadUrl || !key) {
        setIsSubmitting(false);
        setErrorMessage("Failed to fetch upload URL");
        return;
      }

      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: curatedDoc,
        headers: {
          "Content-Type": curatedDoc.type,
        },
      });

      if (!response.ok) {
        setErrorMessage("Failed to upload to presigned URL");
        setIsSubmitting(false);
        return;
      }

      const notifyResponse = await notifyUploadCompletionMutation({
        variables: {
          input: {
            key: key,
            curated: true,
            participant_id: +batchDoc.participant_id!,
          },
        },
      });

      if (!notifyResponse.data?.notifyUploadCompletion?.success) {
        setErrorMessage("Error notifying database: " + notifyResponse.data?.notifyUploadCompletion?.message);
        setIsSubmitting(false);
        return;
      }
      return notifyResponse.data.notifyUploadCompletion.documentId;
    } catch (uploadError) {
      console.log(uploadError);
    }
  }, [batchDoc, curatedDoc, getPresignedUploadURL, notifyUploadCompletionMutation]);
  const determineRequestSource = (curatedDoc: File | undefined, isExternalUser: boolean): RequestSource => {
    if (!isExternalUser) {
      return RequestSource.INTERNAL_USER_FORM_SUBMIT;
    }
    if (curatedDoc) {
      return RequestSource.EXTERNAL_USER_SPREADSHEET;
    }
    return RequestSource.EXTERNAL_USER_FORM_SUBMIT; // For internal, both form submit and spreadsheet submit will use EXTERNAL_USER_FORM_SUBMIT
  };
  const submitResaleBatch = useCallback(
    async (curatedDocumentId: number | undefined) => {
      try {
        const response = await insertResalesMutation({
          variables: {
            resales: resaleData.map((resale) => {
              const isReupload = batchDoc && batchDoc.document_id;
              return {
                artist: resale.artist,
                artwork: resale.artwork,
                resale: resale.resale,
                resale_status: resale.resale_status,
                batch_discriminator: resale.batch_discriminator,
                vendor_id: participant ? participant.participant_id : resale.vendor_id,
                curated_document_id: curatedDocumentId,
                batch_document_id: batchDoc ? batchDoc.document_id : undefined,
                royalty_payable: resale.royalty_payable,
                ipi_basenumber: resale.ipi_basenumber,
                ...(isReupload ? {} : { request_source: determineRequestSource(curatedDoc, isExternalUser) }),
              };
            }),
          },
        });
        if (!response.data || !response.data.insertOrUpdateResalesBatch) {
          setErrorMessage("Failed to insert or update resales.");
          setIsSubmitting(false);
          return;
        }

        const failedResales = response.data?.insertOrUpdateResalesBatch?.resales?.filter((item) => !item?.success);

        if (failedResales && failedResales?.length > 0) {
          //TODO-NRRS-302 failed reports without the artistId should noted to the users
          const filtered = resaleData.filter((reportFormData) =>
            failedResales.find(
              (failedResale) =>
                reportFormData.artist?.artist_id &&
                reportFormData.artist?.artist_id === failedResale?.artistId &&
                reportFormData.artwork?.artwork_name &&
                reportFormData.artwork?.artwork_name === failedResale?.artworkTitle,
            ),
          );
          setResaleData(filtered);
        } else {
          resetList();
          resetBatchDoc();
          resetCuratedDoc();
          resetParticipant();
          setIsSubmitted(true);
        }
        setNumSubmitted(resaleData.length);
        setIsSubmitting(false);
      } catch (insertErrors) {
        console.log("insertErrors" + insertErrors);
        setErrorMessage("An error occurred while submitting the resales.");
        setIsSubmitting(false);
      }
    },
    [
      insertResalesMutation,
      resaleData,
      participant,
      batchDoc,
      curatedDoc,
      isExternalUser,
      setResaleData,
      resetList,
      resetBatchDoc,
      resetCuratedDoc,
      resetParticipant,
    ],
  );

  const updateBatchDocStatus = useCallback(async () => {
    try {
      const response = await reviewedStatusUpdateMutation({
        variables: { input: { document_id: batchDoc.document_id!, reviewed: true } },
      });
      if (!response.data?.updateReviewedStatus?.success) {
        setErrorMessage("Failed to update document reviewed status");
      }
    } catch (documentError) {
      console.log(documentError);
    }
  }, [batchDoc, reviewedStatusUpdateMutation]);

  const submitResales = useCallback(async () => {
    let curatedDocumentId: number | undefined = undefined;
    setIsSubmitting(true);

    if (curatedDoc !== undefined) {
      curatedDocumentId = await uploadCuratedFile();
    }

    await submitResaleBatch(curatedDocumentId);

    if (batchDoc) {
      await updateBatchDocStatus();
    }

    setIsSubmitting(false);
  }, [batchDoc, curatedDoc, uploadCuratedFile, submitResaleBatch, updateBatchDocStatus]);

  const edit = (index: number) => {
    navigate(ROUTES.ResaleSubmission.buildPath({}, { index_id: index }));
  };

  const deleteResale = (index: number) => {
    setResaleData(deleteResalefromIndex(index, resaleData));
  };

  const clearCart = () => {
    resetList();
    setModalShow(false);
  };

  return (
    <Container fluid>
      {errorMessage && (
        <Row className="row d-flex justify-content-center">
          <Col className="col-md-6">
            <div className="alert alert-danger">{errorMessage}</div>
          </Col>
          <Row className="row d-flex justify-content-center">
            <Col>
              <p className={classNames(styles["message"], "b3", "my-4")}>
                {"Failed to Submit these resales; Check that this template is correct for your use case (CMO/Domestic)"}
              </p>
            </Col>
          </Row>
          <Row className="row d-flex justify-content-center">
            <Col>
              <p className={classNames(styles["message"], "b3", "my-4")}>{`Errors: ${errorMessage}`}</p>
            </Col>
          </Row>
        </Row>
      )}
      {!isSubmitted && (
        <>
          <DashboardHeader showResaleCart={false}></DashboardHeader>
          <Row className="mt-5 pt-5">
            <Col className="cartSummary">
              <Row className={classNames(styles["header-row"])}>
                <Col>
                  <h3>Resale report summary</h3>
                  <p>All your reports are saved here for next 30 days</p>
                </Col>
              </Row>
              {batchDoc !== undefined && (
                <Row className="max-width-50%">
                  <Col className={classNames(styles["card-col"])}>
                    <Card className={classNames(styles["card-alt"])}>
                      <Row>
                        <Col>
                          <ReporterDisplay
                            reporter={batchDoc.participant_name}
                            company={batchDoc.participant_company}
                          />
                        </Col>
                        <Col>
                          <FileDisplay s3_location={batchDoc.s3_location!} />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              )}
              {!isCmo && (
                <Row className="py-2">
                  <Col>Want to continue to add this list?</Col>
                  <Col>
                    <IconLink
                      src={PlusIcon}
                      to={ROUTES.ResaleSubmission.path}
                      text={`Add report ${resaleData.length + 1}`}
                    ></IconLink>
                  </Col>
                  <Col>
                    {resales.length > 0 && (
                      <IconLink
                        src={CrossIconBlue}
                        text="Clear all reports"
                        onClick={() => {
                          setModalShow(true);
                        }}
                      />
                    )}
                  </Col>
                </Row>
              )}
              {invalidResales.length > 0 && (
                <>
                  <div className="p-4 secondaryRed b3 bold">
                    Invalid reports that require attention ({invalidResales.length})
                  </div>
                  {invalidResales
                    .slice()
                    .reverse()
                    .map((item, index) => (
                      <CartDrawerContent
                        key={`${item.title}${item.owner}`}
                        {...item}
                        index={resales.length - index}
                        onEditClick={() => edit(resales.length - index - 1)}
                        onDeleteClick={() => deleteResale(resales.length - index - 1)}
                      />
                    ))}
                </>
              )}

              {validResales.length > 0 && (
                <>
                  <div className="px-4 pt-5 pb-2 monoGrey1 b3 bold">All valid resales ({validResales.length})</div>
                  <div className="px-4 monoGrey2 b4 medium">
                    Provisionally eligible resales (
                    {validResales.filter((resale) => resale.resaleStatus === "PROVISIONALLY_ELIGIBLE").length})
                  </div>
                  <div className="px-4 pb-2 monoGrey2 b4 medium">
                    Provisionally ineligible resales (
                    {validResales.filter((resale) => resale.resaleStatus === "PROVISIONALLY_INELIGIBLE").length})
                  </div>
                  {validResales
                    .slice()
                    .reverse()
                    .map((item, index) => (
                      <CartDrawerContent
                        key={`${item.title}${item.owner}`}
                        {...item}
                        index={resales.length - index}
                        onEditClick={() => edit(resales.length - index - 1)}
                        onDeleteClick={() => deleteResale(resales.length - index - 1)}
                      />
                    ))}
                </>
              )}
            </Col>

            <Col className={classNames(styles["submit-panel"], "p-5")}>
              <SubmitResalesDisclaimer
                confirmationButtonText={confirmationButtonText}
                onConfirmation={submitResales}
                inputsValid={allResalesValid()}
                isSubmitting={isSubmitting}
              />
            </Col>
          </Row>
        </>
      )}
      {isSubmitted && (
        <Row className="row d-flex justify-content-center">
          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
          <Col className="col-md-6">
            <ResaleSubmitted
              numSubmitted={numSubmitted}
              buttonRoute={isVAAdmin ? ROUTES.Dashboard.ReportsToReview.path : ROUTES.Dashboard.path}
              buttonText={isVAAdmin ? "Reports to review" : "Back to dashboard"}
              messageText={
                isVAAdmin
                  ? ""
                  : "The resale royalty team will review your submissions. We’ll notify you of resales eligible for royalty collection."
              }
            />
          </Col>
        </Row>
      )}
      <Modal show={modalShow} onHide={() => setModalShow(false)}>
        <Modal.Header closeButton />
        <Modal.Body>Are you sure you remove all reports?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => clearCart()}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export const deleteResalefromIndex = (index: number, resaleData: ValidatedResaleInput[]): ValidatedResaleInput[] => {
  const newResaledata = [...resaleData];
  newResaledata.splice(index, 1);
  return newResaledata;
};
